import { ProviderWorkerReport } from '../../api/server/models/ProviderWorkerReport';
import { ProviderWorkerReportGroup } from '../../api/server/models/ProviderWorkerReportGroup';
import { ProviderWorkerReportMetric } from '../../api/server/models/ProviderWorkerReportMetric';
import { ProviderWorkerReportWorkflow } from '../../api/server/models/ProviderWorkerReportWorkflow';
import { ProviderWorkerReportWorkflowValue } from '../../api/server/models/ProviderWorkerReportWorkflowValue';

/**
 * The Workflows page's arithmetic (FE-167), over `GET /provider/report/worker`. Every rule here is
 * a row of the design's field crosswalk (§6). The UI only draws what these functions return.
 *
 * Three facts measured on sandbox 2026-10-09 shape it, and none of them is visible in the model:
 *
 * - **UNKNOWN ("Paused") is pinned last by VALUE, not by code.** Its `code` came back `"0.000"`, so
 *   sorting by code alone puts it first.
 * - **The catalog arrays only list what the response's population contains.** A drill-down
 *   response's `workflowStatuses` holds one status, so labels always come from the page-load
 *   report, never from a drill response.
 * - **`weekBucket` runs past 9** (to 30 on sandbox). The grid shows 1–9 as designed and ignores
 *   the rest (decided 2026-10-09). The summary cards still count every claim, as the crosswalk
 *   says. So with no filter they equal `totalClaims`, and the grid can hold fewer.
 */

/** The weeks the grid draws. Buckets above this are not drawn (see above). */
export const WEEK_COLUMNS = 9;

/** The status and step value the worker uses for "no state". Labelled "Paused" by the catalog. */
export const UNKNOWN_VALUE = 'UNKNOWN';

/** Sort key for a status with no `code`, per the crosswalk. */
const MISSING_CODE = '999.999';

/** A secondary workflow shows as a corner wedge when it holds more than this share of a cell. */
export const WEDGE_SHARE = 0.03;

/** Every workflow, in the order the legend, the chips and tie-breaks use. */
export const WORKFLOW_ORDER: ProviderWorkerReportWorkflow[] = [
    ProviderWorkerReportWorkflow.Enter,
    ProviderWorkerReportWorkflow.Payer,
    ProviderWorkerReportWorkflow.Provider,
    ProviderWorkerReportWorkflow.Worker,
    ProviderWorkerReportWorkflow.Unassigned,
    ProviderWorkerReportWorkflow.Unknown,
];

export const WORKFLOW_LABELS: Record<ProviderWorkerReportWorkflow, string> = {
    [ProviderWorkerReportWorkflow.Enter]: 'Enter',
    [ProviderWorkerReportWorkflow.Payer]: 'Payer',
    [ProviderWorkerReportWorkflow.Provider]: 'Provider',
    [ProviderWorkerReportWorkflow.Worker]: 'Worker',
    [ProviderWorkerReportWorkflow.Unassigned]: 'Unassigned',
    [ProviderWorkerReportWorkflow.Unknown]: 'Unknown',
};

/** Any value outside the enum renders as Unknown — the crosswalk's fallback. */
export const normalizeWorkflow = (workflow: string | undefined): ProviderWorkerReportWorkflow =>
    WORKFLOW_ORDER.includes(workflow as ProviderWorkerReportWorkflow)
        ? (workflow as ProviderWorkerReportWorkflow)
        : ProviderWorkerReportWorkflow.Unknown;

export interface IWorkerReportFilters {
    /** One workflow, or every workflow when unset. */
    workflow?: ProviderWorkerReportWorkflow;
    /** One step value, or every step when unset. */
    step?: string;
    /** The summary card that is selected. Unset is Total Claims. */
    metric?: ProviderWorkerReportMetric;
}

/** The catalog of labels from the page-load report, keyed by value. */
export interface IWorkerReportCatalog {
    statuses: Map<string, ProviderWorkerReportWorkflowValue>;
    steps: Map<string, ProviderWorkerReportWorkflowValue>;
    actions: Map<string, ProviderWorkerReportWorkflowValue>;
}

export const buildCatalog = (
    report: Pick<ProviderWorkerReport, 'workflowStatuses' | 'workflowSteps' | 'workflowActions'>,
): IWorkerReportCatalog => ({
    statuses: new Map((report.workflowStatuses || []).map(value => [value.value, value])),
    steps: new Map((report.workflowSteps || []).map(value => [value.value, value])),
    actions: new Map((report.workflowActions || []).map(value => [value.value, value])),
});

/** The catalog label, or the raw value when the catalog does not carry it. Never blank. */
export const labelOf = (map: Map<string, ProviderWorkerReportWorkflowValue>, value: string): string =>
    map.get(value)?.label || value;

/**
 * Waiting and In Progress leave out claims whose step is UNKNOWN, on the cards and in the grid
 * when either card is selected (crosswalk §6). On the design's sample, 87,647 of 88,353 in-progress
 * claims are on that step: they are the Paused claims, and Total still counts them.
 */
const countsTowardMetric = (group: ProviderWorkerReportGroup, metric: ProviderWorkerReportMetric): boolean =>
    group.metric === metric && (metric === ProviderWorkerReportMetric.ERROR || group.workflowStep !== UNKNOWN_VALUE);

/** The groups that pass the current filters, every week included. */
export const filterGroups = (
    groups: ProviderWorkerReportGroup[],
    filters: IWorkerReportFilters,
): ProviderWorkerReportGroup[] =>
    groups.filter(
        group =>
            (!filters.workflow || normalizeWorkflow(group.workflow) === filters.workflow) &&
            (!filters.step || group.workflowStep === filters.step) &&
            (!filters.metric || countsTowardMetric(group, filters.metric)),
    );

export interface IWorkerReportSummary {
    total: number;
    inProgress: number;
    waiting: number;
    errors: number;
}

/**
 * The four cards. They follow the Workflow and Step filters but not the selected card, so choosing
 * one card does not zero the others. Every week counts, including weeks the grid does not draw.
 */
export const summaryCounts = (
    groups: ProviderWorkerReportGroup[],
    filters: IWorkerReportFilters,
): IWorkerReportSummary => {
    const scoped = filterGroups(groups, { workflow: filters.workflow, step: filters.step });
    const sum = (predicate: (group: ProviderWorkerReportGroup) => boolean): number =>
        scoped.reduce((total, group) => (predicate(group) ? total + group.count : total), 0);

    return {
        total: sum(() => true),
        inProgress: sum(group => countsTowardMetric(group, ProviderWorkerReportMetric.INPROGRESS)),
        waiting: sum(group => countsTowardMetric(group, ProviderWorkerReportMetric.WAITING)),
        errors: sum(group => countsTowardMetric(group, ProviderWorkerReportMetric.ERROR)),
    };
};

export interface IHeatmapCell {
    /** 1-based, as the API counts it. */
    week: number;
    count: number;
    /** Counts per workflow, every workflow present (zeros included). */
    byWorkflow: Record<ProviderWorkerReportWorkflow, number>;
    /** The largest workflow: the cell's hue. Undefined for an empty cell. */
    dominant?: ProviderWorkerReportWorkflow;
    /** The second-largest workflow, only when it holds more than `WEDGE_SHARE` of the cell. */
    wedge?: ProviderWorkerReportWorkflow;
}

export interface IHeatmapActionRow {
    action: string;
    label: string;
    description?: string;
    cells: IHeatmapCell[];
    total: number;
}

export interface IHeatmapStatusRow {
    status: string;
    label: string;
    description?: string;
    cells: IHeatmapCell[];
    total: number;
    /** Claims in an error action, across the drawn weeks. Zero hides the badge. */
    errors: number;
    actions: IHeatmapActionRow[];
}

export interface IHeatmap {
    rows: IHeatmapStatusRow[];
    /** The largest status-row cell: the top of the shade scale. */
    max: number;
}

const emptyByWorkflow = (): Record<ProviderWorkerReportWorkflow, number> =>
    WORKFLOW_ORDER.reduce(
        (acc, workflow) => ({ ...acc, [workflow]: 0 }),
        {} as Record<ProviderWorkerReportWorkflow, number>,
    );

/** Dominant and wedge from per-workflow counts. Ties go to `WORKFLOW_ORDER`, so the result is stable. */
export const cellHues = (
    byWorkflow: Record<ProviderWorkerReportWorkflow, number>,
): Pick<IHeatmapCell, 'dominant' | 'wedge'> => {
    const ranked = WORKFLOW_ORDER.map(workflow => ({ workflow, count: byWorkflow[workflow] || 0 }))
        .filter(entry => entry.count > 0)
        .sort((a, b) => b.count - a.count || WORKFLOW_ORDER.indexOf(a.workflow) - WORKFLOW_ORDER.indexOf(b.workflow));
    const total = ranked.reduce((sum, entry) => sum + entry.count, 0);
    const second = ranked[1];

    return {
        dominant: ranked[0]?.workflow,
        wedge: second && second.count / total > WEDGE_SHARE ? second.workflow : undefined,
    };
};

const buildCells = (groups: ProviderWorkerReportGroup[]): IHeatmapCell[] =>
    Array.from({ length: WEEK_COLUMNS }, (_unused, index) => {
        const week = index + 1;
        const byWorkflow = emptyByWorkflow();
        let count = 0;
        groups
            .filter(group => group.weekBucket === week)
            .forEach(group => {
                byWorkflow[normalizeWorkflow(group.workflow)] += group.count;
                count += group.count;
            });

        return { week, count, byWorkflow, ...cellHues(byWorkflow) };
    });

const sumCells = (cells: IHeatmapCell[]): number => cells.reduce((sum, cell) => sum + cell.count, 0);

/**
 * Status rows by `code`, compared as strings (the crosswalk says lexicographic). A status without a
 * code sorts as `999.999`. UNKNOWN is pinned last whatever its code says.
 */
export const compareStatuses =
    (catalog: IWorkerReportCatalog) =>
    (a: string, b: string): number => {
        if (a === UNKNOWN_VALUE || b === UNKNOWN_VALUE) {
            return a === b ? 0 : a === UNKNOWN_VALUE ? 1 : -1;
        }
        const codeA = catalog.statuses.get(a)?.code || MISSING_CODE;
        const codeB = catalog.statuses.get(b)?.code || MISSING_CODE;
        if (codeA !== codeB) {
            return codeA < codeB ? -1 : 1;
        }

        return labelOf(catalog.statuses, a).localeCompare(labelOf(catalog.statuses, b));
    };

const groupBy = (groups: ProviderWorkerReportGroup[], key: (group: ProviderWorkerReportGroup) => string) => {
    const map = new Map<string, ProviderWorkerReportGroup[]>();
    groups.forEach(group => {
        const k = key(group);
        const list = map.get(k);
        if (list) {
            list.push(group);
        } else {
            map.set(k, [group]);
        }
    });

    return map;
};

/**
 * The grid. One row per status with a claim in the drawn weeks. Each row expands into one row per
 * action: the same action under several steps is one row. Actions are ordered by count, then label.
 */
export const buildHeatmap = (
    groups: ProviderWorkerReportGroup[],
    catalog: IWorkerReportCatalog,
    filters: IWorkerReportFilters,
): IHeatmap => {
    const drawn = filterGroups(groups, filters).filter(
        group => group.weekBucket >= 1 && group.weekBucket <= WEEK_COLUMNS,
    );
    const byStatus = groupBy(drawn, group => group.workflowStatus);

    const rows = [...byStatus.keys()].sort(compareStatuses(catalog)).map((status): IHeatmapStatusRow => {
        const statusGroups = byStatus.get(status) || [];
        const actions = [...groupBy(statusGroups, group => group.workflowAction).entries()]
            .map(([action, actionGroups]): IHeatmapActionRow => {
                const cells = buildCells(actionGroups);
                return {
                    action,
                    label: labelOf(catalog.actions, action),
                    description: catalog.actions.get(action)?.description,
                    cells,
                    total: sumCells(cells),
                };
            })
            .filter(row => row.total > 0)
            .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
        const cells = buildCells(statusGroups);

        return {
            status,
            label: labelOf(catalog.statuses, status),
            description: catalog.statuses.get(status)?.description,
            cells,
            total: sumCells(cells),
            errors: statusGroups
                .filter(group => group.metric === ProviderWorkerReportMetric.ERROR)
                .reduce((sum, group) => sum + group.count, 0),
            actions,
        };
    });

    return {
        rows: rows.filter(row => row.total > 0),
        max: rows.reduce((max, row) => Math.max(max, ...row.cells.map(cell => cell.count)), 0),
    };
};

/** The steps the Step dropdown offers: only steps present in the report, A–Z by label. */
export const stepOptions = (
    groups: ProviderWorkerReportGroup[],
    catalog: IWorkerReportCatalog,
): { value: string; label: string }[] =>
    [...new Set(groups.map(group => group.workflowStep))]
        .map(value => ({ value, label: labelOf(catalog.steps, value) }))
        .sort((a, b) => a.label.localeCompare(b.label));

/**
 * How strongly a cell is shaded, 0–1: `16% + log(n+1)/log(max+1) × 68%` of its hue. Zero for an empty
 * cell, which renders as the neutral background instead.
 */
export const cellIntensity = (count: number, max: number): number => {
    if (count <= 0 || max <= 0) {
        return 0;
    }

    return 0.16 + (Math.log(count + 1) / Math.log(max + 1)) * 0.68;
};

/** WCAG AA for the cells' 12px numbers: they are not "large text", so 4.5:1. */
export const MIN_TEXT_CONTRAST = 4.5;

const channels = (hex: string): number[] =>
    [0, 2, 4].map(start => parseInt(hex.replace('#', '').slice(start, start + 2), 16));

const luminance = (rgb: number[]): number => {
    const [r, g, b] = rgb.map(value => {
        const c = value / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const ratio = (a: number[], b: number[]): number => {
    const [high, low] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (high + 0.05) / (low + 0.05);
};

/** The colour a `#rrggbb` hue at `alpha` actually shows, drawn over the white card. */
const overWhite = (hex: string, alpha: number): number[] => channels(hex).map(c => 255 * (1 - alpha) + c * alpha);

/** WCAG contrast ratio between text colour `textHex` and `hueHex` drawn at `alpha` over white. */
export const contrastOnFill = (textHex: string, hueHex: string, alpha: number): number =>
    ratio(channels(textHex), overWhite(hueHex, alpha));

export interface ICellPaint {
    /** The shade actually drawn: the cell's intensity, lowered only as far as legibility needs. */
    alpha: number;
    /** Light text (true) or dark text (false), whichever reads better on this fill. */
    lightText: boolean;
}

/**
 * How to draw a cell's number legibly (Copilot on #613). The text colour is chosen from the
 * composited fill's real contrast, not from a single intensity threshold: at full shade white
 * measured 1.61:1 on Unknown and 2.32:1 on Provider, where dark text reads at 7.36 and 5.09. Some
 * hues (Enter, Worker) reach 4.5:1 with neither colour at full shade, so the shade is lowered
 * until one does. Volume still orders the shades; the darkest is a little lighter on those hues.
 */
export const readableCellPaint = (hueHex: string, intensity: number, darkText: string, lightText: string): ICellPaint => {
    let best: ICellPaint = { alpha: intensity, lightText: false };
    for (let alpha = intensity; alpha > 0; alpha = Math.round((alpha - 0.02) * 100) / 100) {
        const light = contrastOnFill(lightText, hueHex, alpha);
        const dark = contrastOnFill(darkText, hueHex, alpha);
        best = { alpha, lightText: light > dark };
        if (Math.max(light, dark) >= MIN_TEXT_CONTRAST) {
            return best;
        }
    }

    return best;
};

/** Bucket 1 is 0–7 days; bucket n is `7(n−1)+1` to `7n`. */
export const weekRange = (week: number): string =>
    week <= 1 ? '0–7 days' : `${7 * (week - 1) + 1}–${7 * week} days`;

export const weekLabel = (week: number): string => `${week}wk`;

/** Page size for claim IDs. The server rejects anything above 100 with a 400. */
export const CLAIM_PAGE_SIZE = 100;

export interface IPagerState {
    /** 1-based first row on the page; 0 when the page is empty. */
    from: number;
    to: number;
    total: number;
    hasPrevious: boolean;
    hasNext: boolean;
}

/** `offset+1`–`offset+ids` of `total`. The next page starts at `offset + ids`, and paging stops at `total`. */
export const pagerState = (page: { offset: number; claimIds: string[]; total: number }): IPagerState => {
    const shown = page.claimIds.length;

    return {
        from: shown ? page.offset + 1 : 0,
        to: page.offset + shown,
        total: page.total,
        hasPrevious: page.offset > 0,
        hasNext: shown > 0 && page.offset + shown < page.total,
    };
};

export interface IActionChip {
    action: string;
    label: string;
    count: number;
}

/** The drawer's action chips: one per action with claims in this status and week, largest first. */
export const actionChips = (
    groups: ProviderWorkerReportGroup[],
    catalog: IWorkerReportCatalog,
    filters: IWorkerReportFilters,
    status: string,
    week: number,
): IActionChip[] => {
    const counts = new Map<string, number>();
    filterGroups(groups, filters)
        .filter(group => group.workflowStatus === status && group.weekBucket === week)
        .forEach(group => counts.set(group.workflowAction, (counts.get(group.workflowAction) || 0) + group.count));

    return [...counts.entries()]
        .filter(([, count]) => count > 0)
        .map(([action, count]) => ({ action, label: labelOf(catalog.actions, action), count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

/** `HUMAN_INTERVENTION_NEEDED` → `Human Intervention Needed`. Enums only — never free text. */
export const titleCase = (value: string | undefined): string | undefined =>
    value
        ? value
              .toLowerCase()
              .split('_')
              .filter(Boolean)
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
        : undefined;
